import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { autoCalibrate, getPlayingSegments } from "refsix-core";
import {
    GpsCalibrationPoints,
    GpsPoint,
    MatchPhone,
    PitchCalibrationMethod,
    Sprint,
} from "refsix-js-models";
import {
    CardMapComponent,
    HeatMapComponent,
    OverlayMapComponent,
    SprintMapComponent,
} from ".";
import { useFitnessDataContext } from "./hooks";
import { UserProfile } from "../models/userProfile";
import { RootState } from "../redux/store";
import { setCalibrationPoints as saveCalibrationPoints } from "../services/fitnessDataSync/fitnessDataSyncService";
import { recalculateStats } from "../services/matchService";
import pitchImage from "../assets/images/pitch.png";
import { t } from "i18next";
import { IonButton, IonIcon, IonText } from "@ionic/react";
import { CalibrationModal } from "../pages/tabs/fixturesSegments/calibration/calibrationModal";
import exampleMatch from "../support/example-result.json";
import "./GpsMapComponent.css";
import {
    calibrationCompleted,
    calibrationStarted,
    trackEvent,
} from "../services/analytics/analyticsService";
import { CompareHeatmapsModal } from "../modals/compareHeatmapsModal";
import { swapHorizontal } from "ionicons/icons";
import { isFeatureAvailableCurrentLanguage } from "../utils/FeatureToggle";

export enum GpsMapType {
    Heatmap,
    SprintMap,
    CardMap,
    recalibrate,
    Compare,
}

interface GpsMapProps {
    match: MatchPhone;
    mapType: GpsMapType;
    sprintsData?: Sprint<number>[];
    testId?: string;
    useDummyData?: boolean;
}

// Auto-calibration (SCORZA-702) is attempted at most once per match across ALL
// GpsMap instances. The stats page renders three at once (heatmap/sprintmap/
// cardmap), and three concurrent setCalibrationPoints writes to the same GPS doc
// deadlock the PouchDB upsert+sync — so this guard is module-scoped (shared
// between instances), not per-component.
const autoCalibrationStarted = new Set<string>();

export const GpsMap: FC<GpsMapProps> = ({
    match,
    mapType,
    sprintsData,
    testId,
    useDummyData,
}) => {
    const userProfile = useSelector(
        (state: RootState) => state.currentUser?.profile
    ) as UserProfile;

    const newMatch = useDummyData
        ? (exampleMatch.match as unknown as MatchPhone)
        : match;

    const [geoPoints, setGeoPoints] = useState<GpsPoint[]>();
    const [calibrationPoints, setCalibrationPoints] =
        useState<GpsCalibrationPoints>();
    const [showCalibrationModal, setShowCalibrationModal] = useState(false);

    const playedSegments =
        (newMatch.matchEvents && getPlayingSegments(newMatch)) || [];
    const [showCompareHeatMapsModal, setShowCompareHeatMapsModal] =
        useState(false);

    const { gpsData, loading, reload } = useFitnessDataContext();

    useEffect(() => {
        if (useDummyData) {
            setGeoPoints(exampleMatch.gps.geoPoints);
            setCalibrationPoints(
                exampleMatch.gps.calibrationPoints as GpsCalibrationPoints
            );
            return;
        }
        if (!gpsData || !gpsData.geoPoints) {
            return;
        }
        setGeoPoints(gpsData.geoPoints);

        if (gpsData.calibrationPoints) {
            setCalibrationPoints(gpsData.calibrationPoints);
            return;
        }

        // No stored calibration — auto-calibrate from the GPS track (SCORZA-702)
        // so the heatmap/sprintmap/cardmap render on first view without the
        // manual 3-tap flow. If the model can't get a viable box (guards fail)
        // it resolves null and the manual calibration overlay stays as fallback.
        const matchId = newMatch._id;
        if (!matchId || !userProfile?.username) {
            return;
        }
        if (autoCalibrationStarted.has(matchId)) {
            return;
        }
        autoCalibrationStarted.add(matchId);

        // The guard entry stays latched only for attempts that reach a real
        // outcome (the model declined the track, or a save was started). A
        // cancelled or crashed attempt unlatches so a later mount can retry —
        // otherwise an unmount mid-flight (incl. StrictMode's dev remount)
        // would silently disable auto-calibration for the rest of the session.
        let cancelled = false;
        let saveStarted = false;
        let declined = false;
        autoCalibrate(gpsData.geoPoints)
            .then((points) => {
                if (!points) {
                    // Deterministic "track can't be boxed" — keep the latch so
                    // we don't re-run the model; the manual CTA is the fallback.
                    declined = true;
                    return;
                }
                if (cancelled) {
                    return; // unlatched by the cleanup below
                }
                setCalibrationPoints(points);
                saveStarted = true;
                saveCalibrationPoints(matchId, userProfile.username, points)
                    .then(() => {
                        // The other GpsMap instance(s) on this page lost the
                        // latch and hold no calibration in local state — they
                        // can only render once the shared fitness context
                        // re-emits gpsData with calibrationPoints. Reload it
                        // explicitly instead of relying on recalculateStats'
                        // Redux write to trigger the re-fetch as a side effect.
                        if (cancelled) {
                            return;
                        }
                        reload();
                        return recalculateStats(newMatch, userProfile);
                    })
                    .catch((err) => {
                        // Persisting is transient-failure-prone (network) —
                        // unlatch so the next view of this match retries.
                        autoCalibrationStarted.delete(matchId);
                        console.warn(
                            "auto-calibration: persisting points failed",
                            err
                        );
                    });
            })
            .catch((err) => {
                autoCalibrationStarted.delete(matchId);
                console.warn("auto-calibration failed", err);
            });

        return () => {
            cancelled = true;
            // Unlatch only attempts that never reached an outcome; a declined
            // track or a started save keeps the latch (once per session).
            if (!saveStarted && !declined) {
                autoCalibrationStarted.delete(matchId);
            }
        };
        // newMatch._id is a dep so a match switch on a reused component
        // instance cancels the in-flight attempt instead of persisting the old
        // track's calibration under the new match's id.
    }, [gpsData, userProfile?.username, newMatch._id]);

    const handleCornersSubmitted = (points: GpsCalibrationPoints) => {
        setShowCalibrationModal(false);

        if (newMatch._id) {
            saveCalibrationPoints(
                newMatch._id,
                userProfile.username,
                points
            ).then(() => recalculateStats(newMatch, userProfile));
        }
    };

    const renderMap = () => {
        if (
            (mapType === GpsMapType.Heatmap ||
                mapType === GpsMapType.Compare) &&
            hasAllDataForHeatmap()
        ) {
            return (
                <HeatMapComponent
                    playingSegments={playedSegments}
                    calibrationPoints={
                        calibrationPoints as GpsCalibrationPoints
                    }
                    geoPoints={geoPoints || []}
                    periodsNo={newMatch.periodsNo as string}
                    testId={testId}
                />
            );
        }
        if (mapType === GpsMapType.SprintMap && hasAllDataForSprintMap()) {
            return (
                <SprintMapComponent
                    playingSegments={playedSegments}
                    calibrationPoints={
                        calibrationPoints as GpsCalibrationPoints
                    }
                    geoPoints={geoPoints || []}
                    periodsNo={newMatch.periodsNo as string}
                    sprints={sprintsData as Sprint<number>[]}
                    match={newMatch}
                    testId={testId}
                />
            );
        }
        if (mapType === GpsMapType.CardMap && hasAllDataForCardMap()) {
            return (
                <CardMapComponent
                    playingSegments={playedSegments}
                    calibrationPoints={
                        calibrationPoints as GpsCalibrationPoints
                    }
                    geoPoints={geoPoints || []}
                    match={newMatch}
                    testId={testId}
                />
            );
        }
    };

    const renderCalibrationOverlay = () => {
        const title =
            mapType === GpsMapType.Heatmap || mapType === GpsMapType.Compare
                ? t("fixture.FixtureSummaryStats.viewHeatmap")
                : mapType === GpsMapType.CardMap
                ? t("fixture.FixtureSummaryStats.viewCardmap")
                : t("fixture.FixtureSummaryStats.viewSprintmap");
        return (
            <OverlayMapComponent
                imageUrl={pitchImage}
                title={title}
                showButton={true}
                onButtonClick={() => {
                    setShowCalibrationModal(true);
                    calibrationStarted(mapType.toString(), match);
                }}
                btnTestId="heatMapOverlayBtn"
            />
        );
    };
    const renderNoGpsDataOverlay = () => (
        <OverlayMapComponent
            imageUrl={pitchImage}
            title={t("fixture.FixtureSummaryStats.errorCode2")}
        />
    );

    const hasNoGpsData = () => !useDummyData && !gpsData && !loading;

    const hasAllDataForHeatmap = () =>
        !!(
            playedSegments &&
            geoPoints &&
            calibrationPoints &&
            newMatch.periodsNo
        );
    const hasAllDataForSprintMap = () =>
        !!(
            playedSegments &&
            geoPoints &&
            calibrationPoints &&
            newMatch.periodsNo &&
            sprintsData
        );
    const hasAllDataForCardMap = () =>
        !!(
            playedSegments &&
            geoPoints &&
            calibrationPoints &&
            newMatch.periodsNo &&
            newMatch.matchEvents
        );
    const hasAllDataForMap = () =>
        ((mapType === GpsMapType.Heatmap || mapType === GpsMapType.Compare) &&
            hasAllDataForHeatmap()) ||
        (mapType === GpsMapType.SprintMap && hasAllDataForSprintMap()) ||
        (mapType === GpsMapType.CardMap && hasAllDataForCardMap());

    return (
        <div
            className={
                hasNoGpsData()
                    ? "gps-map no-gps-data"
                    : hasAllDataForMap()
                    ? "gps-map "
                    : "gps-map needs-calibration"
            }
        >
            {hasNoGpsData()
                ? renderNoGpsDataOverlay()
                : hasAllDataForMap()
                ? renderMap()
                : renderCalibrationOverlay()}
            {calibrationPoints?.calibratedBy ===
                PitchCalibrationMethod.PointNet &&
                mapType !== GpsMapType.Compare && (
                    <IonText
                        className="calibrated-by-scorza"
                        data-testid="calibrated-by-scorza"
                    >
                        {t("fixture.FixtureSummaryStats.calibratedByScorza")}
                    </IonText>
                )}
            {calibrationPoints && mapType !== GpsMapType.Compare && (
                <div
                    className="recalibrate-item item-no-padding-end"
                    style={{
                        justifyContent: isFeatureAvailableCurrentLanguage(
                            "compareHeatmapsButton"
                        )
                            ? "space-between"
                            : "flex-end",
                    }}
                >
                    {mapType === GpsMapType.Heatmap &&
                        isFeatureAvailableCurrentLanguage(
                            "compareHeatmapsButton"
                        ) && (
                            <IonButton
                                size="small"
                                data-testid="compare-heatmaps-button"
                                onClick={() => {
                                    setShowCompareHeatMapsModal(true);
                                    trackEvent("HeatmapCompare", {});
                                }}
                            >
                                <IonIcon icon={swapHorizontal} slot={"start"} />
                                {t(
                                    "fixture.FixtureSummaryStats.compareHeatMaps"
                                )}
                            </IonButton>
                        )}

                    <IonButton
                        fill="clear"
                        size="small"
                        className="adjust-pitch-button"
                        data-testid="adjust-pitch-button"
                        onClick={() => {
                            mapType = GpsMapType.recalibrate;
                            setShowCalibrationModal(true);
                            calibrationStarted(mapType.toString(), match);
                        }}
                    >
                        <IonText className="recalibrate">
                            {t("fixture.FixtureSummaryStats.adjustPitch")}
                        </IonText>
                    </IonButton>
                </div>
            )}

            <CompareHeatmapsModal
                showModal={showCompareHeatMapsModal}
                match={match}
                onCloseModal={() => setShowCompareHeatMapsModal(false)}
            />

            <CalibrationModal
                display={showCalibrationModal}
                gpsPoints={geoPoints || []}
                onCornersSubmited={(points) => {
                    calibrationCompleted(mapType.toString(), match);
                    handleCornersSubmitted(points);
                }}
                onCloseModal={() => setShowCalibrationModal(false)}
            />
        </div>
    );
};
